// eslint-disable-next-line no-unused-vars
import React from 'react';
import PropTypes from 'prop-types';

/** @jsx jsx */
import { jsx } from '@emotion/core';

import BaseTableComponent from '../../components/BaseTableComponent';

const DISPLAY_NAME = 'BaseTableHeader';
export default class BaseGridHeader extends BaseTableComponent {
  static displayName = DISPLAY_NAME;

  constructor(props) {
    super({
      childDisplayName: DISPLAY_NAME,
      ...props
    });
  }

  /**
   * Render row actions e.g. buttons and row selection
   * row selection:
   *  {
      type: 'rowSelection'
      }

      buttons will have shape:
      {
        label: LABEL,
        type: "icon" | "button"
        disabled: [ROWID_1, ...]
        options: {
          ... button props
        }
      }
   */
  renderRowActionHeaders = () => {
    const {
      dataTest,
      rowActionConfig,
      renderRowActionBody,
      tableId,
      thProps,
      setRef,
      getRowActionWidthCSS,
      getStickyColStyle,
      getStickyColClassName,
      isValidRowActionFlag
    } = this.props;
    return rowActionConfig && this.isNonEmptyArray(rowActionConfig)
      ? rowActionConfig.map((rowAction, idx) => {
          const props = thProps(idx); // no col offset
          const body = renderRowActionBody('th', rowAction);
          /**
           * Ref used for td style widths
           */
          return (
            <th
              id={rowAction?.id}
              ref={r => {
                if (r && isValidRowActionFlag(rowAction.type)) {
                  setRef(r, rowAction.type);
                }
              }}
              {...props}
              css={
                isValidRowActionFlag(rowAction.type)
                  ? getRowActionWidthCSS(rowAction)
                  : null
              }
              className={`rowAction ${getStickyColClassName(idx, true)}`.trim()}
              data-parent={tableId}
              data-test={`${rowAction.id}-columnHeader-${dataTest}-cybsTable`}
              data-col={idx + 1}
              data-row={1}
              scope="col"
              style={getStickyColStyle(idx, true)}
            >
              {body}
            </th>
          );
        })
      : null;
  };

  /**
   * Row action config will not have any filters but if filter property is
   * included in header config, the render filter
   *  filter: {
        type: 'filterInput' | 'filterDropdown',
        label: 'LABEL'
      },
   */

  renderTableFilters = () => {
    const {
      dataTest,
      headers,
      rowActionConfig,
      tableId,
      thProps,
      tfProps,
      renderTableItem,
      columnIndexOffset,
      // getRowActionWidthCSS,
      getStickyColStyle,
      setStickyColClassName,
      filters
    } = this.props;
    let rowActionFilters = [];
    // only for props
    if (rowActionConfig && rowActionConfig.length > 0) {
      rowActionFilters = rowActionConfig.map((rowAction, idx) => {
        const elProps = tfProps(idx);
        setStickyColClassName(elProps, idx, true);
        // const body = rowAction && rowAction.label ? rowAction.label : null
        return (
          <th
            data-parent={tableId}
            data-col={idx + 1}
            data-row={2}
            data-test={`noFilter-${idx}-${dataTest}-cybsTable`}
            scope="col"
            style={getStickyColStyle(idx, true)}
            {...elProps}
            // css={getRowActionWidthCSS(rowAction)}
          >
            <div
              style={{
                display: 'flex',
                textAlign: 'center',
                height: '100%',
                alignItems: 'center'
              }}
            />
          </th>
        );
      });
    }
    const offset = columnIndexOffset;
    const activeFilters = [...filters];
    const tempHeaders = [...headers]; // for added properties are not modified in header props

    const mainFilters =
      tempHeaders && tempHeaders.length > 0
        ? tempHeaders.map((tHeader, idx) => {
            const { id, filter } = tHeader;
            const elProps = thProps(idx + offset);

            const hasFilter = !!(filter && filter.type);
            // active filters applied
            const currActiveFilter =
              activeFilters && activeFilters.length
                ? activeFilters.filter(tFilter => tFilter.id === id)
                : null;
            // is there a filter with this current header
            const isActiveFilter =
              currActiveFilter && Array.isArray(currActiveFilter)
                ? currActiveFilter.length > 0
                : null;
            // actual filter
            const currFilter =
              currActiveFilter && currActiveFilter.length > 0
                ? currActiveFilter[0]
                : null;
            // should properties be added for component switch
            const shouldComponentSwitch = currFilter && currFilter.active;
            const addedProperties = {};
            const _addProperties = () => {
              if (isActiveFilter && hasFilter) {
                // addedProperties.active = true;
                const tempFilter = { ...tHeader.filter }; // THIS IS NECESSARY
                // tempFilter.type = this.components.ACTIVE_FILTER;
                tempFilter.width = currFilter.width;
                tempFilter.value = currFilter.value;
                addedProperties.filter = tempFilter;
              }
              if (shouldComponentSwitch) {
                addedProperties.active = true;
                addedProperties.filter.type = this.components.ACTIVE_FILTER;
              }
            };

            _addProperties(); // for active filter or other added properties
            setStickyColClassName(elProps, idx);

            const filterCellInfo = { ...tHeader, ...addedProperties };

            if (
              elProps.className?.includes?.('sticky-col') &&
              filterCellInfo.filter
            ) {
              filterCellInfo.filter.isSticky = true;
            }

            const body = hasFilter
              ? renderTableItem('tf', filterCellInfo, idx)
              : null;
            return (
              <th
                data-parent={tableId}
                data-row={2}
                data-col={idx + offset + 1}
                data-test={`${tHeader.id}-columnFilter-cybsTable`}
                scope="col"
                style={getStickyColStyle(idx)}
                {...elProps}
              >
                {body}
              </th>
            );
          })
        : null;

    return [...rowActionFilters, ...mainFilters];
  };

  render() {
    const {
      dataTest,
      headers,
      theadProps,
      renderTableItem,
      tableId,
      variant,
      hasRowActions
    } = this.props;
    const rowClass = 'gridRowHeader';
    const tableFilters =
      variant === this.types.DISPLAY &&
      headers.some(header => header.filter) ? (
        <tr className="filterRow" data-test={`${tableId}-filterRow-cybsTable`}>
          {this.renderTableFilters()}
        </tr>
      ) : null;
    const theadElProps = theadProps() || {};
    return headers && headers.length > 0 ? (
      <thead
        {...theadElProps}
        data-test={`${tableId}-${dataTest}-gridHeaderContainer-cybsTable`}
      >
        <tr
          className={rowClass}
          data-test={`${tableId}-${dataTest}-headerRow-cybsTable`}
        >
          {/* render row action  */}
          {hasRowActions ? this.renderRowActionHeaders() : null}
          {headers.map((columnHeader, headerIdx) =>
            renderTableItem('th', columnHeader, headerIdx)
          )}
        </tr>
        {tableFilters}
      </thead>
    ) : null;
  }
}

BaseGridHeader.propTypes = {
  /**
   * Function for injecting props into header container row for filters and header labels
   */
  theadProps: PropTypes.func,
  /**
   * Function for injecting props into each header
   */
  thProps: PropTypes.func,
  /**
   * If row action config exists then value is used to calculate virtual row idx passed into base grid component
   */
  columnIndexOffset: PropTypes.number,
  /**
   * Used to render row action flags and row actions e.g. buttons and row selection and any future flags to render
   */
  rowActionConfig: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    options: PropTypes.object // for button
  }),
  /**
   * Renderer function for row action table elements
   */
  renderRowActionBody: PropTypes.func,
  /**
   * Renderer function for standard table elements
   */
  renderTableItem: PropTypes.func.isRequired,
  /**
   * Main header object configuration
   */
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      filter: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      }),
      width: PropTypes.number,
      sortable: PropTypes.bool
    })
  ).isRequired,
  /**
   * Table id used for dom markup
   */
  tableId: PropTypes.string.isRequired
};
BaseGridHeader.defaultProps = {
  thProps: () => {},
  theadProps: () => {},
  columnIndexOffset: 0,
  renderRowActionBody: () => null
};
